import dayjs from "dayjs";
import { QA_FEED_CACHE } from "./QaFeedConfig";
import type {
  QaFeedCacheScope,
  QaFeedCacheSnapshot,
  QaFeedQuestion,
} from "./QaFeedType";

const DT = "YYYY-MM-DD HH:mm:ss";

function cacheStorageKey(scope: QaFeedCacheScope): string {
  return `${QA_FEED_CACHE.keyPrefix}.v${QA_FEED_CACHE.version}:${scope.customerId}:${scope.spaceId}:${scope.spaceProductId}:${scope.tab}`;
}

export function trimQaFeedItems(items: QaFeedQuestion[]): QaFeedQuestion[] {
  if (items.length <= QA_FEED_CACHE.maxItems) {
    return items;
  }
  return items.slice(0, QA_FEED_CACHE.maxItems);
}

/** 用服务端第一页刷新列表头部，保留本地已加载的更早分页 */
export function mergeQaFeedFirstPage(
  existing: QaFeedQuestion[],
  page1: QaFeedQuestion[]
): QaFeedQuestion[] {
  const page1Ids = new Set(page1.map((q) => q.id));
  const tail = existing.filter((q) => !page1Ids.has(q.id));
  return trimQaFeedItems([...page1, ...tail]);
}

function writeSnapshot(snapshot: QaFeedCacheSnapshot): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  const trimmed = trimQaFeedItems(snapshot.items);
  const wasTrimmed = trimmed.length < snapshot.items.length;
  const toSave: QaFeedCacheSnapshot = {
    ...snapshot,
    version: QA_FEED_CACHE.version,
    items: trimmed,
    total: Math.max(snapshot.total, trimmed.length),
    pageNum: snapshot.pageNum,
    updatedAt: dayjs().format(DT),
  };
  if (wasTrimmed) {
    toSave.total = Math.max(toSave.total, trimmed.length);
  }
  try {
    localStorage.setItem(cacheStorageKey(snapshot), JSON.stringify(toSave));
  } catch {
    if (trimmed.length <= QA_FEED_CACHE.maxItems / 2) {
      return;
    }
    const half = trimQaFeedItems(trimmed.slice(0, Math.floor(trimmed.length / 2)));
    try {
      localStorage.setItem(
        cacheStorageKey(snapshot),
        JSON.stringify({
          ...toSave,
          items: half,
          pageNum: 1,
        })
      );
    } catch {
      // ignore quota errors
    }
  }
}

export function loadQaFeedCache(scope: QaFeedCacheScope): QaFeedCacheSnapshot | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  const raw = localStorage.getItem(cacheStorageKey(scope));
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as QaFeedCacheSnapshot;
    if (parsed.version !== QA_FEED_CACHE.version) {
      localStorage.removeItem(cacheStorageKey(scope));
      return null;
    }
    if (
      parsed.customerId !== scope.customerId ||
      parsed.spaceId !== scope.spaceId ||
      parsed.spaceProductId !== scope.spaceProductId ||
      parsed.tab !== scope.tab
    ) {
      return null;
    }
    if (!Array.isArray(parsed.items)) {
      return null;
    }
    return {
      ...parsed,
      items: parsed.items,
      total: typeof parsed.total === "number" ? parsed.total : parsed.items.length,
      pageNum: typeof parsed.pageNum === "number" && parsed.pageNum >= 1 ? parsed.pageNum : 1,
    };
  } catch {
    localStorage.removeItem(cacheStorageKey(scope));
    return null;
  }
}

export function saveQaFeedCache(
  scope: QaFeedCacheScope,
  items: QaFeedQuestion[],
  total: number,
  pageNum: number
): void {
  writeSnapshot({
    version: QA_FEED_CACHE.version,
    customerId: scope.customerId,
    spaceId: scope.spaceId,
    spaceProductId: scope.spaceProductId,
    tab: scope.tab,
    items,
    total,
    pageNum,
    updatedAt: dayjs().format(DT),
  });
}

export function updateQaFeedCacheItem(
  scope: QaFeedCacheScope,
  questionId: string,
  patch: Partial<QaFeedQuestion>
): void {
  const cached = loadQaFeedCache(scope);
  if (!cached?.items.length) {
    return;
  }
  const next = cached.items.map((row) =>
    row.id === questionId ? { ...row, ...patch } : row
  );
  writeSnapshot({
    ...cached,
    items: next,
  });
}

export function prependQaFeedCacheItem(
  scope: QaFeedCacheScope,
  item: QaFeedQuestion,
  total: number
): void {
  const cached = loadQaFeedCache(scope);
  const existing = cached?.items ?? [];
  const filtered = existing.filter((q) => q.id !== item.id);
  const merged = trimQaFeedItems([item, ...filtered]);
  saveQaFeedCache(scope, merged, total, 1);
}

export function clearQaFeedCache(scope: QaFeedCacheScope): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  localStorage.removeItem(cacheStorageKey(scope));
}
